import React, { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { DifficultyAgreement, SubtopicOfFocusSymptomNode } from 'src/generated/webapp_gql';
import { Assessment } from 'src/modules/assessments/components/Assessment';
import {
    FieldType,
    MappedAnswers,
    MultiSelectQuestion,
    Question,
} from 'src/modules/assessments/models/assessments';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useOnboardingGuard } from 'src/shared/hooks/useOnboardingGuard';
import { getLoadingEffects } from 'src/shared/selectors/sharedSelectors';
import { color, font } from 'src/shared/styles/variables';

import { BrandWrapper } from '../components/BrandWrapper';
import { useAddSymptomsExperiencedMutation } from '../documents/addSymptomsExperiencedMutation.generated';
import { useSelectDifficultyMutation } from '../documents/selectDifficultyMutation.generated';
import { useSelectPrimaryFocusMutation } from '../documents/selectPrimaryFocusMutation.generated';
import { OnboardingWellnessSurveyPageTestId } from './testIds';
import { captureException } from 'src/shared/helpers/captureException';

const Header = styled.h2(
    {
        color: color.pickledBluewood,
        marginRight: '3rem',
        marginLeft: '3rem',
        maxWidth: '500px',
    },
    font.black(font.size.extraLarge),
);

const SubHeader = styled.h3(
    {
        color: color.baliHai,
        marginBottom: '1rem',
    },
    font.medium(font.size.large),
);

export const OnboardingWellnessSurveyPage: React.FC<RouteComponentProps> = ({ history }) => {
    const shouldRender = useOnboardingGuard();
    const dispatch: Dispatch = useDispatch();
    const { careRecommendations } = useSelector(getUserInformation);
    const { user } = useSelector(getLoadingEffects);

    const { t } = useTranslation();

    const focusOnKey = '6978888';
    const difficultyKey = '697822288';

    const [addSymptomsExperiencedMutation] = useAddSymptomsExperiencedMutation();
    const [selectDifficultyMutation] = useSelectDifficultyMutation();
    const [selectPrimaryFocusMutation] = useSelectPrimaryFocusMutation();

    const topicSelections = careRecommendations[0]?.topicSelections || [];

    const subTopicSelections = careRecommendations[0]?.topicSelections.flatMap(
        topic => topic.subtopicSelections,
    );

    const wellnessSurveySelectedTopicsQuestionText = useMemo(() => {
        const translationPath = topicSelections.length > 1 ? 'plural' : 'singular';
        const beginningText = t(
            `onboarding:onboarding_wellness_survey_page.selected_topics_question_beginning_${translationPath}`,
        );
        const topicSelectionsText = `(<span style="text-decoration:underline">${topicSelections
            .flatMap(topic =>
                topic.subtopicSelections.map(subTopic => subTopic.subtopicOfFocus.name),
            )
            .join(', ')}</span>)`;
        const endingText = t(
            `onboarding:onboarding_wellness_survey_page.selected_topics_question_ending_${translationPath}`,
        );

        return `${beginningText} ${topicSelectionsText} ${endingText}`;
    }, [t, topicSelections]);

    useEffect(() => {
        dispatch.user.fetchUserInfo();
    }, [dispatch.user]);

    const questions: Question[] = [
        ...topicSelections.flatMap(topic => {
            return topic.subtopicSelections.map(selection => {
                return {
                    questionKey: selection.uuid,
                    type: FieldType.MultiSelect,
                    optional: false,
                    displayCrisisWarning: false,
                    keyword: selection.subtopicOfFocus.name,
                    questionText: `${t(
                        'onboarding:onboarding_wellness_survey_page.survey_question_beginning',
                    )} <span style="text-decoration:underline">
                    ${selection.subtopicOfFocus.name},</span> ${t(
                        'onboarding:onboarding_wellness_survey_page.survey_question_ending',
                    )} `,
                    responseType: selection.subtopicOfFocus.subtopicSymptoms.map(
                        (symptom: SubtopicOfFocusSymptomNode) => {
                            return {
                                answerText: symptom.name,
                                value: symptom.uuid,
                            };
                        },
                    ),
                };
            });
        }),
        ...(subTopicSelections?.length > 1
            ? [
                  {
                      questionKey: focusOnKey,
                      type: FieldType.SingleSelect,
                      optional: false,
                      displayCrisisWarning: false,
                      questionText: t('onboarding:onboarding_wellness_survey_page.first_focus'),
                      responseType: topicSelections.flatMap(topic => {
                          return topic.subtopicSelections.map(subTopic => {
                              return {
                                  answerText: subTopic.subtopicOfFocus.name,
                                  value: subTopic.uuid,
                              };
                          });
                      }),
                  },
              ]
            : []),
        {
            questionKey: difficultyKey,
            type: FieldType.SingleSelect,
            optional: false,
            displayCrisisWarning: false,
            questionText: wellnessSurveySelectedTopicsQuestionText,
            responseType: [
                {
                    answerText: t('onboarding:onboarding_wellness_survey_page.strongly_disagree'),
                    value: DifficultyAgreement.StronglyDisagree,
                },
                {
                    answerText: t('onboarding:onboarding_wellness_survey_page.disagree'),
                    value: DifficultyAgreement.Disagree,
                },
                {
                    answerText: t(
                        'onboarding:onboarding_wellness_survey_page.neither_disagree_or_agree',
                    ),
                    value: DifficultyAgreement.NeitherDisagreeOrAgree,
                },
                {
                    answerText: t('onboarding:onboarding_wellness_survey_page.agree'),
                    value: DifficultyAgreement.Agree,
                },
                {
                    answerText: t('onboarding:onboarding_wellness_survey_page.strongly_agree'),
                    value: DifficultyAgreement.StronglyAgree,
                },
            ],
        },
    ];

    const handleSubmit = async (answers: MappedAnswers) => {
        try {
            // for the first three questions send add the symptoms they are experiencing
            await Promise.all(
                Object.keys(answers)
                    .filter(key => Array.isArray(answers[key]))
                    .map(async key => {
                        return addSymptomsExperiencedMutation({
                            variables: {
                                subtopicSelectionUuid: key,
                                symptomsExperienced: [],
                                subtopicSymptoms: {
                                    symptomsUuids: answers[key] as string[],
                                },
                            },
                        });
                    }),
            );

            await selectPrimaryFocusMutation({
                variables: {
                    subtopicSelectionUuid:
                        subTopicSelections?.length > 1
                            ? answers[focusOnKey]
                            : subTopicSelections[0].uuid,
                },
            });

            await selectDifficultyMutation({
                variables: {
                    uuid: careRecommendations[0].uuid,
                    difficultyAgreement: answers[difficultyKey] as DifficultyAgreement,
                },
            });
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);

            captureException(error, {
                error_info: 'Error: failed to submit a new symptom assessment',
            });
        } finally {
            history.push('/a-few-questions');
        }
    };

    return shouldRender && !user.fetchUserInfo ? (
        <BrandWrapper>
            <Assessment
                assessmentName="onboarding_wellness"
                questions={questions}
                onSubmit={handleSubmit}
                onBack={() => history.goBack()}
                customHeader={(question: MultiSelectQuestion) => (
                    <>
                        <Header
                            data-testid={OnboardingWellnessSurveyPageTestId.Header}
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(question.questionText),
                            }}
                        ></Header>

                        {question.type === FieldType.MultiSelect && (
                            <SubHeader>
                                {t(
                                    'onboarding:onboarding_wellness_survey_page.select_all_that_apply',
                                )}
                            </SubHeader>
                        )}
                    </>
                )}
            />
        </BrandWrapper>
    ) : null;
};
